import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Clock, AlertTriangle, Camera, Loader, UserCheck, ChevronDown } from "lucide-react";
import { groundOpsApi } from "@/api";

const STATUS_OPTS = [
  { value: "in_progress", label: "Start Task",     color: "bg-blue-500 hover:bg-blue-400"    },
  { value: "completed",   label: "Mark Complete",  color: "bg-green-500 hover:bg-green-400"  },
  { value: "delayed",     label: "Flag Delay",     color: "bg-red-500 hover:bg-red-400"      },
  { value: "blocked",     label: "Report Blocked", color: "bg-purple-500 hover:bg-purple-400"},
];

const DELAY_REASONS = [
  "Equipment not available", "Staff shortage", "Vendor not on stand",
  "Aircraft access issue", "Weather condition", "Prior task not complete",
  "Documentation pending", "Other",
];

// Role label → staff role values that can do this task
const ROLE_STAFF_MAP = {
  "Ramp Agent":           ["ramp_agent", "supervisor"],
  "Baggage Handler":      ["baggage_handler", "supervisor"],
  "Fueling Coordinator":  ["fueling_coordinator", "supervisor"],
  "Cleaning Staff":       ["cleaning_staff", "supervisor"],
  "Catering Staff":       ["catering_staff", "supervisor"],
  "Dispatcher":           ["dispatcher", "supervisor"],
  "Engineer":             ["engineer", "supervisor"],
};

export default function RampTaskUpdate({ user }) {
  const { taskId } = useParams();
  const navigate   = useNavigate();

  const [task, setTask]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [comments, setComments]   = useState("");
  const [delayReason, setDelay]   = useState("");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  // Staff assignment
  const [allStaff, setAllStaff]       = useState([]);
  const [assignOpen, setAssignOpen]   = useState(false);
  const [assigning, setAssigning]     = useState(false);
  const [assignDone, setAssignDone]   = useState(false);

  useEffect(() => {
    groundOpsApi.getMyTasks(user.role, user.station)
      .then(tasks => {
        const found = tasks.find(t => t.id === taskId);
        if (found) setTask(found);
      })
      .finally(() => setLoading(false));

    // Also fetch all staff at station for reassignment
    groundOpsApi.getStaff(user.station)
      .then(setAllStaff)
      .catch(() => {});
  }, [taskId, user]);

  async function handleUpdate(status) {
    setSaving(true);
    try {
      await groundOpsApi.updateTask(taskId, {
        status,
        comments: comments || delayReason || "",
        delay_minutes: status === "delayed" ? 15 : 0,
      });
      setSaved(true);
      setTimeout(() => navigate("/ramp/tasks"), 1500);
    } finally { setSaving(false); }
  }

  async function handleAssign(staffName) {
    setAssigning(true);
    try {
      await groundOpsApi.assignTask(taskId, staffName);
      setTask(t => ({ ...t, assigned_to: staffName }));
      setAssignDone(true);
      setTimeout(() => { setAssignOpen(false); setAssignDone(false); }, 1200);
    } finally { setAssigning(false); }
  }

  if (loading) return <div className="p-6 text-center text-slate-500 animate-pulse">Loading task...</div>;
  if (!task)   return (
    <div className="p-6 text-center">
      <div className="text-slate-400 mb-4">Task not found — may already be completed</div>
      <button onClick={() => navigate("/ramp/tasks")} className="text-orange-400 text-sm font-bold border border-orange-500/30 rounded-xl px-4 py-2">
        Back to Tasks
      </button>
    </div>
  );

  // Candidates for reassignment: match task role
  const taskRoleValues = ROLE_STAFF_MAP[task.role] || [];
  const candidates = allStaff.filter(s => taskRoleValues.includes(s.role));

  return (
    <div className="p-4 max-w-sm mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 text-sm mb-4">
        <ArrowLeft size={16} /> Back
      </button>

      {saved ? (
        <div className="text-center py-12">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-3" />
          <p className="text-white font-bold text-lg">Updated!</p>
          <p className="text-slate-500 text-sm">Returning to tasks...</p>
        </div>
      ) : (
        <>
          {/* Task header */}
          <div className="bg-[#0c1929] rounded-2xl p-4 mb-4 border border-slate-700/30">
            <div className="text-[9px] text-orange-400 uppercase tracking-widest font-bold mb-1">{task.flight_number} · {task.route}</div>
            <h2 className="text-lg font-black text-white mb-0.5">{task.name}</h2>
            <div className="text-[11px] text-slate-500">Stand {task.stand} · Due {new Date(task.planned_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })}</div>
            {task.vendor && task.vendor !== "—" && (
              <div className="text-[10px] text-slate-600 mt-1">Vendor: {task.vendor}</div>
            )}

            {/* Current assignment + reassign */}
            <div className="mt-3 pt-3 border-t border-slate-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[9px] text-slate-600 uppercase tracking-widest font-bold">Assigned To</div>
                  <div className="text-sm font-semibold text-white mt-0.5">{task.assigned_to || "Unassigned"}</div>
                  <div className="text-[9px] text-slate-600 mt-0.5">Role: {task.role}</div>
                </div>
                {candidates.length > 0 && (
                  <button
                    onClick={() => setAssignOpen(!assignOpen)}
                    className="flex items-center gap-1.5 text-[10px] font-bold text-orange-400 border border-orange-500/30 bg-orange-500/10 px-3 py-2 rounded-xl hover:bg-orange-500/20 transition"
                    data-testid="reassign-btn"
                  >
                    <UserCheck size={12} />
                    Reassign
                    <ChevronDown size={10} className={`transition-transform ${assignOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>

              {/* Staff picker */}
              {assignOpen && (
                <div className="mt-3 space-y-1.5" data-testid="staff-picker">
                  <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-2">
                    Available Staff at {user.station}
                  </div>
                  {assignDone && (
                    <div className="flex items-center gap-2 text-green-400 text-xs font-bold py-1">
                      <CheckCircle size={12} /> Reassigned successfully
                    </div>
                  )}
                  {candidates.map(s => (
                    <button
                      key={s.id}
                      onClick={() => handleAssign(s.name)}
                      disabled={assigning || s.name === task.assigned_to}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm transition-all disabled:opacity-40
                        ${s.name === task.assigned_to
                          ? "bg-orange-500/10 border-orange-500/30 text-orange-400"
                          : "bg-[#070f1d] border-slate-600/30 text-slate-300 hover:border-orange-500/30 hover:text-white"}`}
                      data-testid={`assign-staff-${s.id}`}
                    >
                      <div className="text-left">
                        <div className="font-semibold">{s.name}</div>
                        <div className="text-[9px] text-slate-500 capitalize">{s.role.replace(/_/g, " ")}</div>
                      </div>
                      {s.name === task.assigned_to && (
                        <span className="text-[9px] text-orange-400 font-bold uppercase">Current</span>
                      )}
                      {assigning && s.name !== task.assigned_to && (
                        <Loader size={12} className="animate-spin text-slate-500" />
                      )}
                    </button>
                  ))}
                  {candidates.length === 0 && (
                    <div className="text-xs text-slate-600 py-2 text-center">No matching staff at {user.station}</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 mb-4">
            {STATUS_OPTS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleUpdate(opt.value)}
                disabled={saving}
                className={`w-full py-4 rounded-2xl text-white font-black text-base transition-all active:scale-95 disabled:opacity-50 ${opt.color}`}
                data-testid={`action-${opt.value}`}
              >
                {saving ? <Loader size={18} className="animate-spin mx-auto" /> : opt.label}
              </button>
            ))}
          </div>

          {/* Delay reason + comments */}
          <div className="bg-[#0c1929] rounded-2xl p-4 border border-slate-700/30 space-y-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Delay Reason (if applicable)</label>
              <select
                value={delayReason}
                onChange={e => setDelay(e.target.value)}
                className="w-full bg-[#070f1d] border border-slate-600/40 rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-orange-500/50"
                data-testid="delay-reason-select"
              >
                <option value="">Select reason...</option>
                {DELAY_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Comments</label>
              <textarea
                value={comments}
                onChange={e => setComments(e.target.value)}
                placeholder="Add details..."
                rows={3}
                className="w-full bg-[#070f1d] border border-slate-600/40 rounded-xl px-3 py-3 text-white text-sm resize-none focus:outline-none focus:border-orange-500/50 placeholder-slate-700"
                data-testid="task-comments"
              />
            </div>

            {/* Photo upload */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-widest font-bold block mb-2">Photo Evidence</label>
              <label className="flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-600/30 border-dashed text-slate-500 text-sm cursor-pointer hover:border-orange-500/30 hover:text-orange-400 transition-colors">
                <Camera size={16} />
                <span>Attach photo</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" />
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
